import { styled } from "../../../styles";

/**
 * Permission checklist (EN-5129). The catalog gains an entry per migrated
 * Retool page — 32 by the end of the EN-5146 epic — so the list is grouped by
 * the sidebar section each page belongs to, and the rows are dense: a bordered
 * card per permission in one column fitted five before scrolling.
 */

/** Filter row above the groups. Sticky so it survives a long scroll. */
export const ChecklistToolbar = styled("div", {
  position: "sticky",
  top: 0,
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  padding: "4px 0 10px",
  backgroundColor: "$white",
});

export const ChecklistSearch = styled("input", {
  flex: 1,
  height: "34px",
  padding: "0 10px",
  borderRadius: "6px",
  border: "1px solid $gray-40",
  fontFamily: "$openSans",
  fontSize: "13px",
  color: "$charcoal",

  "&:focus": {
    outline: "none",
    borderColor: "$primary",
  },

  "&::placeholder": {
    color: "$gray-60",
  },
});

/** "6 of 16 granted" — the count the flat list never showed. */
export const ChecklistSummary = styled("span", {
  fontFamily: "$openSans",
  fontSize: "12px",
  color: "$gray-80",
  whiteSpace: "nowrap",
});

export const ChecklistContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  maxHeight: "420px",
  overflowY: "auto",
  padding: "0 2px",
});

export const ChecklistGroup = styled("section", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
});

export const ChecklistGroupHeader = styled("div", {
  display: "flex",
  alignItems: "baseline",
  gap: "8px",
  padding: "0 0 2px",
  borderBottom: "1px solid $gray-30",
});

export const ChecklistGroupName = styled("h4", {
  margin: 0,
  fontFamily: "$openSans",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "$gray-90",
});

export const ChecklistGroupCount = styled("span", {
  fontFamily: "$openSans",
  fontSize: "11px",
  color: "$gray-60",
});

/**
 * Two columns where the modal is wide enough. Keyed off the container rather
 * than the theme's `@md`/`@sm` aliases, which are viewport-width and would
 * fire on every laptop.
 */
export const ChecklistGrid = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "2px 12px",

  "@media screen and (max-width: 720px)": {
    gridTemplateColumns: "minmax(0, 1fr)",
  },
});

export const ChecklistItem = styled("label", {
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  padding: "5px 6px",
  borderRadius: "6px",
  cursor: "pointer",

  "&:hover": {
    backgroundColor: "$gray-20",
  },

  "& input": {
    marginTop: "2px",
    accentColor: "$primary",
    cursor: "pointer",
    flexShrink: 0,
  },

  variants: {
    disabled: {
      true: {
        opacity: 0.6,
        cursor: "not-allowed",
        "& input": { cursor: "not-allowed" },
      },
    },
    /** Granted by the role — shown checked but not editable here. */
    locked: {
      true: {
        cursor: "default",
        "&:hover": { backgroundColor: "transparent" },
        "& input": { cursor: "default" },
      },
    },
  },
});

export const ChecklistItemText = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "1px",
  minWidth: 0,
});

export const ChecklistItemCode = styled("span", {
  fontFamily: "monospace",
  fontSize: "12.5px",
  color: "$charcoal",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const ChecklistItemLabel = styled("span", {
  fontFamily: "$openSans",
  fontSize: "11.5px",
  color: "$gray-80",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

/** Marks a permission the role grants, next to its code. */
export const ChecklistInheritedTag = styled("span", {
  marginLeft: "6px",
  padding: "1px 6px",
  borderRadius: "8px",
  fontFamily: "$openSans",
  fontSize: "10px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  backgroundColor: "$primary-20",
  color: "$primary",
  verticalAlign: "middle",
});

/** Shown when a search matches nothing. */
export const ChecklistEmpty = styled("p", {
  margin: 0,
  padding: "16px 6px",
  fontFamily: "$openSans",
  fontSize: "13px",
  color: "$gray-80",
});

export const TypeBadge = styled("span", {
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: "10px",
  fontSize: "12px",
  fontWeight: 600,
  fontFamily: "$openSans",

  variants: {
    userType: {
      Staff: {
        backgroundColor: "rgba(37, 86, 219, 0.1)",
        color: "#2556DB",
      },
      Patient: {
        backgroundColor: "$primary-20",
        color: "$primary",
      },
      Other: {
        backgroundColor: "$gray-30",
        color: "$gray-90",
      },
    },
  },

  defaultVariants: {
    userType: "Other",
  },
});

export const RoleCodeCell = styled("span", {
  fontFamily: "monospace",
  fontSize: "13px",
  color: "$charcoal",
});

export const SectionLabel = styled("h3", {
  fontFamily: "$openSans",
  fontSize: "13px",
  fontWeight: 600,
  color: "$gray-90",
  margin: "8px 0 0 0",
});
